// wall-clock time of the instant in the zone, encoded as a Date.UTC value
const getZoneWallClockMs = (utcMs: number, timeZoneId: string): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZoneId,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(utcMs);
    const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0);
    // 'hour' is '24' at midnight in some ICU versions; normalize.
    const hour = get('hour') % 24;
    return Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
};

// Range values are wall-clock datetimes in the display zone ("YYYY-MM-DD" or
// "YYYY-MM-DDTHH:mm:ss"); offsets the browser-local picker appends are dropped by design.
const WALL_CLOCK_REGEX = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

interface WallClockParts {
    date: string;
    hour: number;
    minute: number;
    second: number;
    hasTime: boolean;
}

export const parseWallClockInput = (value: string): WallClockParts | null => {
    if (!value) {
        return null;
    }
    const match = WALL_CLOCK_REGEX.exec(value);
    if (!match?.[1]) {
        return null;
    }
    const probe = new Date(`${match[1]}T00:00:00Z`);
    if (!Number.isFinite(probe.getTime())) {
        return null;
    }
    return {
        date: match[1],
        hour: match[2] != null ? Number(match[2]) : 0,
        minute: match[3] != null ? Number(match[3]) : 0,
        second: match[4] != null ? Number(match[4]) : 0,
        hasTime: match[2] != null,
    };
};

const padTwo = (value: number): string => String(value).padStart(2, '0');

export const normalizeWallClock = (value: string): string | null => {
    const parts = parseWallClockInput(value);
    if (!parts) {
        return null;
    }
    return parts.hasTime
        ? `${parts.date}T${padTwo(parts.hour)}:${padTwo(parts.minute)}:${padTwo(parts.second)}`
        : parts.date;
};

export const expandWallClock = (value: string, boundary: 'start' | 'end'): string | null => {
    const parts = parseWallClockInput(value);
    if (!parts) {
        return null;
    }
    if (parts.hasTime) {
        return `${parts.date}T${padTwo(parts.hour)}:${padTwo(parts.minute)}:${padTwo(parts.second)}`;
    }
    return boundary === 'start' ? `${parts.date}T00:00:00` : `${parts.date}T23:59:59`;
};

export const wallClockToUnixSeconds = (
    value: string,
    boundary: 'start' | 'end',
    timeZoneId?: string,
): number | null => {
    const parts = parseWallClockInput(value);
    if (!parts) {
        return null;
    }
    const [ year, month, day ] = parts.date.split('-').map(Number);
    const hour = parts.hasTime ? parts.hour : (boundary === 'start' ? 0 : 23);
    const minute = parts.hasTime ? parts.minute : (boundary === 'start' ? 0 : 59);
    const second = parts.hasTime ? parts.second : (boundary === 'start' ? 0 : 59);
    const wallMs = Date.UTC(year, month - 1, day, hour, minute, second, 0);

    if (!timeZoneId || timeZoneId === 'UTC') {
        return Math.floor(wallMs / 1000);
    }

    // two-pass inverse lookup keeps DST transitions on the correct offset
    let utcGuess = wallMs - (getZoneWallClockMs(wallMs, timeZoneId) - wallMs);
    utcGuess = wallMs - (getZoneWallClockMs(utcGuess, timeZoneId) - utcGuess);
    return Math.floor(utcGuess / 1000);
};

export const epochToZonedWallClock = (unixSeconds: number, timeZoneId?: string): string => {
    const utcMs = unixSeconds * 1000;
    const wallMs = !timeZoneId || timeZoneId === 'UTC' ? utcMs : getZoneWallClockMs(utcMs, timeZoneId);
    return new Date(wallMs).toISOString()
        .slice(0, 19);
};

export const getRangeUnixSeconds = (
    range: { start: string; end: string },
    timeZoneId?: string,
): { startDate: number; endDate: number } | null => {
    const startDate = wallClockToUnixSeconds(range.start, 'start', timeZoneId);
    const endDate = wallClockToUnixSeconds(range.end, 'end', timeZoneId);

    if (startDate == null || endDate == null || !Number.isFinite(startDate) || !Number.isFinite(endDate)) {
        return null;
    }

    return {
        startDate,
        endDate,
    };
};
